import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import Header from "../../components/ui/Header";
import Footer from "../../components/Footer";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import { ayurvedaService } from "../../services/ayurvedaService";

// FIXED: Define animation variants outside component to prevent re-creation on every render
const fadeInUpVariants = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 }
};

const fadeInLeftVariants = {
  initial: { opacity: 0, x: -30 },
  whileInView: { opacity: 1, x: 0 }
};

const TreatmentPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [treatment, setTreatment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI States for interactive sections
  const [activeTimelineStep, setActiveTimelineStep] = useState(0);
  const [selectedDoshaAssessment, setSelectedDoshaAssessment] = useState(null);
  const [activeFaqIndex, setActiveFaqIndex] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Helper component for empty section message
  const EmptySectionMessage = ({ icon = "AlertTriangle", message = "Content under construction. Check back soon!" }) => (
    <div className="glass-card p-12 text-center">
      <Icon name={icon} size={48} className="text-warning mx-auto mb-4 opacity-50" />
      <p className="text-lg text-muted-foreground font-medium">{message}</p>
      <p className="text-sm text-muted-foreground mt-2">
        We're working on bringing you comprehensive information about this treatment.
      </p>
    </div>
  );

  // FIXED: Wrap loadTreatment in useCallback to prevent infinite loops
  const loadTreatment = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ayurvedaService?.getCompleteTreatmentPage(slug);

      if (response?.data) {
        setTreatment(response?.data);

        // Log panchakarma protocol data once when loaded
        const panchakarmaData = response?.data?.centerPage?.panchakarma?.weeklyProtocol ||
          response?.data?.centerPage?.panchakarma?.protocol ||
          response?.data?.centerPage?.panchakarma;

        if (panchakarmaData && Array.isArray(panchakarmaData)) {
          console.log('Panchakarma Protocol loaded:');
          panchakarmaData.forEach((weekData, index) => {
            console.log(`Week ${index + 1}:`, weekData);
          });
        }
      } else {
        setError('Treatment not found');
      }
    } catch (err) {
      console.error('Failed to load treatment:', err);
      setError(err?.message || 'Failed to load treatment details');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  // FIXED: Load treatment data when slug changes
  useEffect(() => {
    loadTreatment();
  }, [loadTreatment]);

  // OPTIMIZED: Setup mouse tracking with throttling to reduce re-renders
  useEffect(() => {
    let rafId = null;
    let lastUpdate = 0;
    const throttleMs = 16; // ~60fps

    const handleMouseMove = (e) => {
      const now = Date.now();

      // Throttle updates to avoid excessive re-renders
      if (now - lastUpdate < throttleMs) {
        return;
      }

      lastUpdate = now;

      // Use requestAnimationFrame for smooth updates
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        setMousePosition({
          x: (e?.clientX / window.innerWidth) * 100,
          y: (e?.clientY / window.innerHeight) * 100
        });
      });
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Return cleanup function to remove event listener
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []); // Empty dependency array - only setup once on mount

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground text-lg">Loading treatment details...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !treatment) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 flex flex-col justify-center items-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            <Icon name="AlertCircle" size={64} className="text-error mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-3 text-foreground">Treatment Not Found</h2>
            <p className="text-muted-foreground mb-6">
              {error || `No treatment found matching: ${slug}`}
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/treatments')} iconName="ArrowLeft" iconPosition="left">
                View All Treatments
              </Button>
              <Button variant="outline" onClick={() => navigate('/')} iconName="Home" iconPosition="left">
                Go Home
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Extract data from merged response
  const centerPage = treatment?.centerPage || {};
  const {
    heroTitle = treatment?.title,
    heroSubtitle = treatment?.short_description,
    overview = treatment?.long_description,
    benefits = [],
    causes = [],
    symptoms = [],
    ayurvedaView = treatment?.ayurvedic_explanation,
    panchakarma = {},
    assessmentInfo = null,
    treatmentProcess = [],
    faqs = [],
    whyUs = [],
    seo = {}
  } = centerPage;

  // ADDED: Extract panchakarma data more flexibly
  const panchakarmaProtocol = panchakarma?.weeklyProtocol ||
    (typeof panchakarma === 'string' ? null :
      Object.keys(panchakarma || {})?.length > 0 ? panchakarma : null);
  // ADDED: Check if we have any panchakarma content
  const hasPanchakarmaData = () => {
    if (Array.isArray(panchakarmaProtocol)) {
      return panchakarmaProtocol?.length > 0;
    }
    if (typeof panchakarmaProtocol === 'object' && panchakarmaProtocol !== null) {
      return Object.keys(panchakarmaProtocol)?.length > 0;
    }
    if (typeof panchakarma === 'string' && panchakarma?.trim()) {
      return true;
    }
    return false;
  };

  // Helper function to check if assessment data exists
  const hasAssessmentData = () => {
    return assessmentInfo &&
      typeof assessmentInfo === 'object' &&
      Object.keys(assessmentInfo)?.length > 0 &&
      assessmentInfo?.questions?.length > 0;
  };

  // Check if we have any content at all
  const hasAnyContent = benefits?.length > 0 || causes?.length > 0 || symptoms?.length > 0 ||
    faqs?.length > 0 || whyUs?.length > 0 || treatmentProcess?.length > 0 ||
    panchakarma?.length > 0 || overview || ayurvedaView;

  return (
    <>
      <Helmet>
        <title>{seo?.title || `${treatment?.title} - Treatment Center`}</title>
        <meta name="description" content={seo?.description || treatment?.seo_description} />
        <meta name="keywords" content={seo?.keywords || treatment?.tags?.join(', ')} />
        <meta property="og:title" content={seo?.title || treatment?.title} />
        <meta property="og:description" content={seo?.description || treatment?.short_description} />
        <meta property="og:image" content={treatment?.main_image_url || '/default-treatment.jpg'} />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-24">
          {/* ====================================================================
              HERO SECTION WITH FLOATING GLASS PANELS
              ==================================================================== */}
          <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-background via-primary/5 to-secondary/10">
            {/* Animated Background */}
            <div className="absolute inset-0">
              <motion.div
                className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-primary/10 to-secondary/10 blur-3xl"
                style={{
                  left: `${mousePosition?.x * 0.02}%`,
                  top: `${mousePosition?.y * 0.02}%`
                }}
                animate={{
                  scale: [1, 1.2, 1],
                  rotate: [0, 180, 360]
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            </div>

            {/* Main Hero Content */}
            <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="inline-block bg-primary/10 text-primary px-6 py-3 rounded-full text-sm font-medium mb-6">
                  🌿 {treatment?.category?.name || 'Ayurvedic Treatment'}
                </div>

                <motion.h1
                  className="font-headline text-4xl md:text-6xl lg:text-7xl font-bold mb-8 text-morph"
                  style={{
                    background: 'linear-gradient(45deg, var(--color-primary), var(--color-accent), var(--color-secondary), var(--color-primary))',
                    backgroundSize: '300% 300%',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                  animate={{
                    backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                  {heroTitle}
                </motion.h1>

                <p className="text-md md:text-md text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
                  {heroSubtitle}
                </p>

                <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
                  <Link to="/specialist-booking">
                    <Button
                      size="lg"
                      className="ayurveda-gradient text-white font-semibold px-10 py-5 text-xl shadow-floating"
                      iconName="Calendar"
                      iconPosition="left"
                      iconSize={24}
                    >
                      Book Doctor Consultation
                    </Button>
                  </Link>

                  {/* FIXED: Conditional button rendering based on assessment data */}
                  {hasAssessmentData() ? (
                    <Link to={`/treatments/${slug}/assessment`}>
                      <Button
                        size="lg"
                        variant="outline"
                        className="border-primary text-primary hover:bg-primary/10 font-semibold px-10 py-5 text-xl"
                        iconName="Clipboard"
                        iconPosition="left"
                        iconSize={24}
                      >
                        Take Self-Assessment
                      </Button>
                    </Link>
                  ) : (
                    <Link to="/specialist-booking">
                      <Button
                        size="lg"
                        variant="outline"
                        className="border-accent text-accent hover:bg-accent/10 font-semibold px-10 py-5 text-xl"
                        iconName="Phone"
                        iconPosition="left"
                        iconSize={24}
                      >
                        Schedule Consultation
                      </Button>
                    </Link>
                  )}
                </div>

                {/* Trust Indicators - ENHANCED with 4 cards including Trust Shield */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                  {treatment?.success_rate_percentage && (
                    <motion.div
                      className="glass-card p-6 text-center"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.6 }}
                      whileHover={{ scale: 1.05, y: -5 }}
                    >
                      <Icon name="TrendingUp" size={28} className="text-primary mx-auto mb-2" />
                      <div className="text-3xl font-bold text-primary mb-1">
                        {treatment?.success_rate_percentage}%
                      </div>
                      <div className="text-sm text-muted-foreground">Success Rate</div>
                    </motion.div>
                  )}

                  <motion.div
                    className="glass-card p-6 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.7 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                  >
                    <Icon name="Users" size={28} className="text-secondary mx-auto mb-2" />
                    <div className="text-3xl font-bold text-secondary mb-1">
                      100%
                    </div>
                    <div className="text-sm text-muted-foreground">Personalized Care</div>
                  </motion.div>

                  {treatment?.expected_duration && (
                    <motion.div
                      className="glass-card p-6 text-center"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.8 }}
                      whileHover={{ scale: 1.05, y: -5 }}
                    >
                      <Icon name="Clock" size={28} className="text-primary mx-auto mb-2" />
                      <div className="text-xl font-bold text-primary mb-1">
                        {treatment?.expected_duration}
                      </div>
                      <div className="text-sm text-muted-foreground">Treatment Duration</div>
                    </motion.div>
                  )}

                  <motion.div
                    className="glass-card p-6 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.9 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                  >
                    <Icon name="Shield" size={28} className="text-success mx-auto mb-2" />
                    <div className="text-3xl font-bold text-success mb-1">
                      100%
                    </div>
                    <div className="text-sm text-muted-foreground">Safe & Certified</div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* ====================================================================
              OVERVIEW SECTION - ALWAYS SHOW WITH FALLBACK
              ==================================================================== */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-4xl mx-auto"
              >
                <h2 className="font-headline text-4xl md:text-5xl font-bold mb-8 text-primary text-center">
                  Understanding the Condition
                </h2>
                {overview ? (
                  <div className="glass-card p-8">
                    <p className="text-tertiary text-lg text-muted-foreground leading-relaxed whitespace-pre-wrap">
                      {overview}
                    </p>
                  </div>
                ) : (
                  <EmptySectionMessage
                    icon="FileText"
                    message="Detailed overview coming soon!"
                  />
                )}
              </motion.div>
            </div>
          </section>

          {/* ====================================================================
              BENEFITS SECTION - ALWAYS SHOW WITH FALLBACK
              ==================================================================== */}
          <section className="py-20 bg-gradient-to-r from-muted/30 to-background">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center mb-16"
              >
                <h2 className="font-headline text-4xl md:text-5xl font-bold mb-6 text-primary">
                  Treatment Benefits
                </h2>
              </motion.div>

              {benefits?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                  {benefits?.map((benefit, index) => (
                    <motion.div
                      key={index}
                      className="glass-card p-6"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: index * 0.1 }}
                      whileHover={{ scale: 1.02, y: -5 }}
                    >
                      <div className="flex items-start gap-4">
                        <Icon name="CheckCircle" size={24} className="text-success flex-shrink-0 mt-1" />
                        <p className="text-foreground font-medium">{benefit}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <EmptySectionMessage
                  icon="Heart"
                  message="Treatment benefits information is being compiled!"
                />
              )}
            </div>
          </section>

          {/* ====================================================================
              SYMPTOMS & CAUSES SECTIONS - ALWAYS SHOW WITH FALLBACK
              ==================================================================== */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
                {/* Symptoms */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <h3 className="font-headline text-2xl font-bold mb-8 text-primary flex items-center gap-3">
                    <Icon name="AlertCircle" size={28} />
                    Common Symptoms
                  </h3>
                  {symptoms?.length > 0 ? (
                    <div className="space-y-4">
                      {symptoms?.map((symptom, index) => (
                        <motion.div
                          key={index}
                          className="glass-card p-4 flex items-center gap-3"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                          <Icon name="Activity" size={20} className="text-error flex-shrink-0" />
                          <p className="text-foreground">{symptom}</p>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <EmptySectionMessage
                      icon="Activity"
                      message="Symptom information coming soon!"
                    />
                  )}
                </motion.div>

                {/* Causes */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <h3 className="font-headline text-2xl font-bold mb-8 text-primary flex items-center gap-3">
                    <Icon name="HelpCircle" size={28} />
                    Root Causes
                  </h3>
                  {causes?.length > 0 ? (
                    <div className="space-y-4">
                      {causes?.map((cause, index) => (
                        <motion.div
                          key={index}
                          className="glass-card p-4 flex items-center gap-3"
                          initial={{ opacity: 0, x: 20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                          <Icon name="Target" size={20} className="text-warning flex-shrink-0" />
                          <p className="text-foreground">{cause}</p>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <EmptySectionMessage
                      icon="Target"
                      message="Root cause information coming soon!"
                    />
                  )}
                </motion.div>
              </div>
            </div>
          </section>

          {/* ====================================================================
              AYURVEDA VIEW SECTION - ALWAYS SHOW WITH FALLBACK
              ==================================================================== */}
          <section className="py-20 bg-gradient-to-br from-secondary/10 to-muted/20">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-4xl mx-auto"
              >
                <h2 className="font-headline text-4xl md:text-5xl font-bold mb-8 text-primary text-center">
                  Ayurvedic Perspective
                </h2>
                {ayurvedaView ? (
                  <div className="glass-card p-8">
                    <p className="text-lg text-muted-foreground leading-relaxed whitespace-pre-wrap">
                      {ayurvedaView}
                    </p>
                  </div>
                ) : (
                  <EmptySectionMessage
                    icon="BookOpen"
                    message="Ayurvedic perspective content under development!"
                  />
                )}
              </motion.div>
            </div>
          </section>

          {/* ====================================================================
              PANCHAKARMA PROTOCOLS - ENHANCED TO HANDLE DIFFERENT DATA STRUCTURES
              ==================================================================== */}
          <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
              <motion.div
                variants={fadeInUpVariants}
                initial="initial"
                whileInView="whileInView"
                transition={{ duration: 0.8 }}
                className="text-center mb-16"
              >
                <h2 className="font-headline text-4xl md:text-5xl font-bold mb-6 text-primary">
                  Specialized Panchakarma Protocol
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  A systematic week-by-week approach to deep healing and rejuvenation
                </p>
              </motion.div>

              {hasPanchakarmaData() ? (
                <div className="max-w-5xl mx-auto">
                  {/* ENHANCED: Handle array of weekly protocols */}
                  {Array.isArray(panchakarmaProtocol) && panchakarmaProtocol?.length > 0 ? (
                    <div className="relative">
                      {/* Progress Line */}
                      <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-primary via-accent to-secondary"></div>

                      {/* Week-by-Week Timeline */}
                      <div className="space-y-12">
                        {panchakarmaProtocol?.map((weekData, index) => (
                          <motion.div
                            key={index}
                            className="relative pl-20"
                            variants={fadeInLeftVariants}
                            initial="initial"
                            whileInView="whileInView"
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: index * 0.15 }}
                          >
                            {/* Week Marker */}
                            <motion.div
                              className="absolute left-4 w-16 h-16 rounded-full bg-gradient-to-br from-primary to-accent border-4 border-white shadow-lg flex items-center justify-center"
                              whileHover={{ scale: 1.15, rotate: 5 }}
                            >
                              <div className="text-center">
                                <div className="text-white text-xs font-semibold">Week</div>
                                <div className="text-white text-lg font-bold">{weekData?.week || index + 1}</div>
                              </div>
                            </motion.div>

                            {/* Week Content Card */}
                            <div className="glass-card p-8 hover:shadow-2xl transition-all duration-300">
                              <div className="flex items-start justify-between mb-4">
                                <h3 className="font-headline text-2xl font-bold text-primary">
                                  {weekData?.title}
                                </h3>
                                {weekData?.phase && (
                                  <span className="text-sm font-medium text-accent bg-accent/10 px-4 py-2 rounded-full">
                                    {weekData?.phase}
                                  </span>
                                )}
                              </div>

                              {weekData?.description && (
                                <p className="text-muted-foreground mb-6 text-lg leading-relaxed">
                                  {weekData?.description}
                                </p>
                              )}
                              {weekData?.benefits && (
                                <p className="text-muted-foreground mb-6 text-lg leading-relaxed">
                                  {weekData?.benefits.map((benefit, index) => (
                                    <div key={index}>
                                      ❀ {benefit}
                                    </div>
                                  ))
                                  }
                                </p>
                              )}

                              {/* Treatment Steps */}
                              {weekData?.treatments && weekData?.treatments?.length > 0 && (
                                <div className="space-y-3">
                                  <h4 className="font-semibold text-lg text-primary mb-3 flex items-center gap-2">
                                    <Icon name="Zap" size={20} />
                                    Key Treatments
                                  </h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {weekData?.treatments?.map((treatment, tIndex) => (
                                      <div key={tIndex} className="flex items-center gap-3 bg-muted/30 p-3 rounded-lg">
                                        <Icon name="CheckCircle2" size={18} className="text-success flex-shrink-0" />
                                        <span className="text-sm font-medium">{treatment}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Expected Outcomes */}
                              {weekData?.outcomes && weekData?.outcomes?.length > 0 && (
                                <div className="mt-6 p-4 bg-success/5 border border-success/20 rounded-lg">
                                  <h4 className="font-semibold text-sm uppercase tracking-wide text-success mb-2 flex items-center gap-2">
                                    <Icon name="Target" size={16} />
                                    Expected Outcomes
                                  </h4>
                                  <ul className="space-y-1">
                                    {weekData?.outcomes?.map((outcome, oIndex) => (
                                      <li key={oIndex} className="text-sm text-muted-foreground flex items-start gap-2">
                                        <Icon name="ArrowRight" size={14} className="mt-1 text-success flex-shrink-0" />
                                        {outcome}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  ) : typeof panchakarma === 'string' && panchakarma?.trim() ? (
                    /* ADDED: Handle string-based panchakarma protocol */
                    (<div className="glass-card p-8">
                      <div className="prose prose-lg max-w-none">
                        <p className="text-lg text-muted-foreground leading-relaxed whitespace-pre-wrap">
                          {panchakarma}
                        </p>
                      </div>
                    </div>)
                  ) : typeof panchakarmaProtocol === 'object' && panchakarmaProtocol !== null ? (
                    /* ADDED: Handle object-based panchakarma (legacy format) */
                    (<div className="glass-card p-8">
                      <div className="space-y-6">
                        {Object.entries(panchakarmaProtocol)?.map(([key, value], index) => (
                          <motion.div
                            key={key}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            className="border-l-4 border-primary pl-6 py-2"
                          >
                            <h3 className="text-xl font-bold text-primary mb-2 capitalize">
                              {key?.replace(/_/g, ' ')}
                            </h3>
                            <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                              {typeof value === 'string' ? value : JSON.stringify(value)}
                            </p>
                          </motion.div>
                        ))}
                      </div>
                    </div>)
                  ) : (
                    <EmptySectionMessage
                      icon="Calendar"
                      message="Panchakarma protocol data is being populated!"
                    />
                  )}
                </div>
              ) : (
                <EmptySectionMessage
                  icon="Calendar"
                  message="Week-wise Panchakarma protocol is being prepared by our expert physicians!"
                />
              )}
            </div>
          </section>

          {/* ====================================================================
              TREATMENT PROCESS - HEALING ROADMAP - ENHANCED DATA HANDLING
              ==================================================================== */}
          {/* <section className="py-20 bg-gradient-to-r from-muted/30 to-background">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center mb-16"
              >
                <h2 className="font-headline text-4xl md:text-5xl font-bold mb-6 text-primary">
                  Your Healing Roadmap
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  A structured timeline showing your journey from assessment to complete recovery
                </p>
              </motion.div> */}

          {/* FIXED: Use same panchakarmaProtocol check as above */}
          {/* {hasPanchakarmaData() && Array.isArray(panchakarmaProtocol) && panchakarmaProtocol?.length > 0 ? (
                <div className="max-w-4xl mx-auto">
                  <div className="relative"> */}
          {/* Progress Line */}
          {/* <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-primary via-accent to-success"></div> */}

          {/* Timeline Steps - Populated from panchakarmaProtocol */}
          {/* <div className="space-y-16">
                      {panchakarmaProtocol?.map((step, index) => (
                        <motion.div
                          key={index}
                          className="relative pl-20 cursor-pointer"
                          initial={{ opacity: 0, x: -30 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.8, delay: index * 0.2 }}
                          onClick={() => setActiveTimelineStep(index)}
                        > */}
          {/* Milestone Marker */}
          {/* <motion.div
                            className={`absolute left-6 w-8 h-8 rounded-full border-4 border-white shadow-lg flex items-center justify-center ${activeTimelineStep >= index
                              ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
                              }`}
                            whileHover={{ scale: 1.2 }}
                          >
                            <span className="text-sm font-bold">{step?.week || index + 1}</span>
                          </motion.div> */}

          {/* Step Content */}
          {/* <div className={`glass-card p-8 transition-all duration-300 ${activeTimelineStep === index ? 'ring-2 ring-primary shadow-xl' : 'hover:shadow-lg'
                            }`}>
                            <div className="flex items-start justify-between mb-4">
                              <h3 className="font-headline text-xl font-bold text-primary">
                                {step?.title}
                              </h3>
                              {step?.phase && (
                                <span className="text-sm font-medium text-accent bg-accent/10 px-4 py-2 rounded-full">
                                  {step?.phase}
                                </span>
                              )}
                            </div> 

                            <p className="text-muted-foreground mb-6 leading-relaxed whitespace-pre-wrap">
                              {step?.description}
                            </p>*/}

          {/* Treatment Milestones */}
          {/* {step?.treatments && step?.treatments?.length > 0 && (
                              <div className="space-y-3">
                                <h4 className="font-semibold text-sm text-primary mb-3 flex items-center gap-2">
                                  <Icon name="CheckCircle2" size={18} />
                                  Key Treatments
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {step?.treatments?.map((treatment, tIndex) => (
                                    <div key={tIndex} className="flex items-center gap-3 bg-muted/20 p-3 rounded-lg">
                                      <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
                                      <span className="text-sm">{treatment}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )} */}

          {/* Expected Outcomes */}
          {/* {step?.outcomes && step?.outcomes?.length > 0 && (
                              <div className="mt-6 p-4 bg-success/5 border border-success/20 rounded-lg">
                                <h4 className="font-semibold text-sm uppercase tracking-wide text-success mb-3 flex items-center gap-2">
                                  <Icon name="Target" size={16} />
                                  Expected Outcomes
                                </h4>
                                <ul className="space-y-2">
                                  {step?.outcomes?.map((outcome, oIndex) => (
                                    <li key={oIndex} className="text-sm text-muted-foreground flex items-start gap-2">
                                      <Icon name="ArrowRight" size={14} className="mt-1 text-success flex-shrink-0" />
                                      {outcome}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div> */}

          {/* Recovery Progress Indicator */}
          {/* <motion.div
                    className="mt-12 p-6 bg-gradient-to-r from-primary/5 to-success/5 rounded-2xl"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-primary flex items-center gap-2">
                        <Icon name="TrendingUp" size={20} />
                        Recovery Journey Progress
                      </h3>
                      <div className="text-2xl font-bold text-primary">
                        {panchakarmaProtocol?.length || 0} Phases
                      </div>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      Your personalized treatment roadmap spans {panchakarmaProtocol?.length || 0} carefully planned phases,
                      each designed to build upon the previous stage for optimal healing and lasting recovery.
                    </p>
                  </motion.div>
                </div>
              ) : (
                <EmptySectionMessage
                  icon="MapPin"
                  message="Your personalized healing roadmap is being prepared by our expert physicians!"
                />
              )}
            </div>
          </section> */}

          {/* Recovery Progress Indicator */}
          <motion.div
            className="mt-12 p-6 bg-gradient-to-r from-primary/5 to-success/5 rounded-2xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex flex-col items-center text-center">
              {/* Heading with icon */}
              <h3 className="text-lg font-semibold text-primary flex items-center gap-2 mb-3">
                <Icon name="TrendingUp" size={20} />
                Recovery Journey Progress
              </h3>

              {/* Description text */}
              <p className="text-muted-foreground text-md leading-relaxed mb-6 max-w-3xl">
                Your personalized treatment roadmap spans {panchakarmaProtocol?.length || 4} carefully planned phases,
                each designed to build upon the previous stage for optimal healing and lasting recovery.
              </p>


            </div>
          </motion.div>

          {/* ====================================================================
              FAQ SECTION - ALWAYS SHOW WITH FALLBACK
              ==================================================================== */}
          < section className="py-20 bg-background" >
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center mb-16"
              >
                <h2 className="font-headline text-4xl md:text-5xl font-bold mb-6 text-primary">
                  Frequently Asked Questions
                </h2>
              </motion.div>

              {faqs?.length > 0 ? (
                <div className="max-w-4xl mx-auto space-y-4">
                  {faqs?.map((faq, index) => (
                    <motion.div
                      key={index}
                      className="glass-card overflow-hidden"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: index * 0.1 }}
                    >
                      <button
                        className="w-full p-6 text-left flex items-center justify-between hover:bg-primary/5 transition-colors"
                        onClick={() => setActiveFaqIndex(activeFaqIndex === index ? null : index)}
                      >
                        <span className="font-semibold text-lg pr-4">{faq?.question}</span>
                        <motion.div
                          animate={{ rotate: activeFaqIndex === index ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Icon name="ChevronDown" size={20} className="text-primary" />
                        </motion.div>
                      </button>

                      <AnimatePresence>
                        {activeFaqIndex === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="p-6 pt-0 text-muted-foreground leading-relaxed">
                              {faq?.answer}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <EmptySectionMessage
                  icon="HelpCircle"
                  message="FAQ section is being compiled with expert answers!"
                />
              )}
            </div>
          </section >

          {/* ====================================================================
              WHY CHOOSE US - ALWAYS SHOW WITH FALLBACK
              ==================================================================== */}
          < section className="py-20 bg-gradient-to-br from-muted/20 to-secondary/10" >
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center mb-16"
              >
                <h2 className="font-headline text-4xl md:text-5xl font-bold mb-6 text-primary">
                  Why Choose Us
                </h2>
              </motion.div>

              {whyUs?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                  {whyUs?.map((point, index) => (
                    <motion.div
                      key={index}
                      className="glass-card p-6 text-center"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: index * 0.1 }}
                      whileHover={{ scale: 1.05, y: -5 }}
                    >
                      <Icon name="Award" size={40} className="text-primary mx-auto mb-4" />
                      <p className="text-foreground font-medium">{point?.point || point}</p>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <EmptySectionMessage
                  icon="Award"
                  message="Our unique advantages and approach details coming soon!"
                />
              )}
            </div>
          </section >

          {/* ====================================================================
              CTA SECTION - ALWAYS SHOW
              ==================================================================== */}
          < section className="py-20 bg-gradient-to-br from-primary to-secondary" >
            <div className="container mx-auto px-4 text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-3xl mx-auto"
              >
                <h2 className="font-headline text-4xl md:text-5xl font-bold mb-6 text-white">
                  Ready to Transform Your Life?
                </h2>
                <p className="text-xl text-white/90 mb-8">
                  Book your specialized assessment with our expert Ayurvedic doctors and begin your journey to natural healing.
                </p>

                <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                  <Link to="/specialist-booking">
                    <Button
                      size="lg"
                      className="bg-white text-primary hover:bg-white/90 font-semibold px-10 py-5 text-xl"
                      iconName="Calendar"
                      iconPosition="left"
                      iconSize={24}
                    >
                      Book Doctor Consultation
                    </Button>
                  </Link>

                  <Button
                    variant="outline"
                    size="lg"
                    className="border-white text-white hover:bg-white/10 px-10 py-5 text-xl"
                    iconName="Phone"
                    iconPosition="left"
                    iconSize={24}
                    onClick={() => window.open('tel:+917842202255', '_self')}
                  >
                    Call for Immediate Help
                  </Button>
                </div>
              </motion.div>
            </div>
          </section >
        </main >

        <Footer />
      </div >
    </>
  );
};

export default TreatmentPage;